import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AnySubscriptionType, SubscriptionType } from '../../../types/misc';
import Community from '../../../types/community';
import { isSubscriptionGreaterThanOrEqual } from '../../../utils/subscriptionUtils';
import { businessSubscriptionService } from '../../../services/business/subscriptionService';
import ModalLayout from '../layouts/ModalLayout';
import { SubscriptionSelector } from '../selection-lists/SubscriptionSelector';
import AreYouSureModal from './AreYouSureModal';
import Button from '../buttons/Button';

interface ChangeSubCommunitySubscriptionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  community: Community;
  businessId: number;
  onSubscriptionChanged: (newType: SubscriptionType) => void;
}

export default function ChangeSubCommunitySubscriptionModal({
  open,
  setOpen,
  community,
  businessId,
  onSubscriptionChanged,
}: ChangeSubCommunitySubscriptionModalProps): JSX.Element {
  const { t } = useTranslation();

  const currentType: SubscriptionType =
    (community.subscription?.type as SubscriptionType | undefined) ?? SubscriptionType.FREE;

  const [selectedType, setSelectedType] = useState<SubscriptionType>(currentType);
  const [loading, setLoading] = useState(false);
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);

  // Reset selection whenever modal opens
  useEffect(() => {
    if (open) setSelectedType(currentType);
  }, [open]);

  const handleCancel = () => {
    setSelectedType(currentType);
    setOpen(false);
  };

  const handleSave = () => {
    if (selectedType === currentType) {
      setOpen(false);
      return;
    }

    const isDowngrade = isSubscriptionGreaterThanOrEqual(currentType, selectedType);
    if (isDowngrade) {
      setDowngradeWarningOpen(true);
    } else {
      commitChange(selectedType);
    }
  };

  const commitChange = async (newType: SubscriptionType) => {
    setLoading(true);
    try {
      await businessSubscriptionService.changeSubCommunitySubscription(businessId, community.id, newType);
      toast.success(t('toast.success.subscriptionChanged'));
      setOpen(false);
      onSubscriptionChanged(newType);
    } catch (error) {
      console.error('Failed to change subscription:', error);
      toast.error(t('toast.error.changeSubscription'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalLayout
        open={open}
        setOpen={setOpen}
        className="relative transform overflow-hidden rounded-[12px] bg-secondary-50 px-6 py-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl">
        <div className="mb-6">
          <h3 className="text-xl font-semibold">
            {t('component.modal.changeSubCommunitySubscription.title')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{community.name}</p>
        </div>

        <SubscriptionSelector
          selectedSubscription={selectedType as AnySubscriptionType}
          mode="selectable"
          onSubscriptionSelect={(type) => setSelectedType(type as SubscriptionType)}
          showTrialPeriod={false}
          isBusiness={false}
          isSubCommunity
        />

        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
            className="rounded-full px-5 py-2">
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading || selectedType === currentType}
            className="rounded-full px-5 py-2">
            {loading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            ) : (
              t('general.save')
            )}
          </Button>
        </div>
      </ModalLayout>

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={() => {
          setDowngradeWarningOpen(false);
          commitChange(selectedType);
        }}
        onCancel={() => setDowngradeWarningOpen(false)}
      />
    </>
  );
}